import React, { useEffect, useState } from "react";
import { Box, Typography, useTheme, useMediaQuery, keyframes } from "@mui/material";
import { motion } from "framer-motion";
import { useInView } from "react-intersection-observer";
import GroupIcon from "@mui/icons-material/Group";
import AssessmentIcon from "@mui/icons-material/Assessment";
import TranslateIcon from "@mui/icons-material/Translate";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";

// Shiny hover effect animation
const shine = keyframes`
  0% {
    transform: translateX(-100%) translateY(-100%) rotate(45deg);
  }
  100% {
    transform: translateX(200%) translateY(200%) rotate(45deg);
  }
`;

/**
 * Count Up Animation Component
 */
const CountUp = ({ end, suffix = "", duration = 2, isInView }) => {
  const [count, setCount] = useState(0);

  useEffect(() => {
    if (!isInView) return;

    let startTime = null;
    const startValue = 0;
    let animationFrameId = null;

    const animate = (currentTime) => {
      if (!startTime) startTime = currentTime;
      const progress = Math.min((currentTime - startTime) / (duration * 1000), 1);
      
      // Smoother easing function (easeOutCubic)
      const easeOutCubic = 1 - Math.pow(1 - progress, 3);
      const currentCount = startValue + (end - startValue) * easeOutCubic;
      
      // For decimal values (like 99.9), keep one decimal place
      if (end % 1 !== 0) {
        setCount(parseFloat(currentCount.toFixed(1)));
      } else {
        setCount(Math.floor(currentCount));
      }

      if (progress < 1) {
        animationFrameId = requestAnimationFrame(animate);
      } else {
        if (end % 1 !== 0) {
          setCount(end);
        } else {
          setCount(end);
        }
      }
    };

    animationFrameId = requestAnimationFrame(animate);

    return () => {
      if (animationFrameId) {
        cancelAnimationFrame(animationFrameId);
      }
    };
  }, [end, duration, isInView]);

  // Format large numbers with commas
  const formatNumber = (num) => {
    if (num % 1 !== 0) {
      return num.toFixed(1);
    }
    return num.toLocaleString();
  };

  return (
    <span>
      {formatNumber(count)}{suffix}
    </span>
  );
};

/**
 * Statistics Section
 * 
 * Four theme-matching metric boxes with count-up animation
 */
const StatisticsSection = () => {
  const theme = useTheme();
  const isSmallScreen = useMediaQuery(theme.breakpoints.down("md"));
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const { ref, inView } = useInView({
    threshold: 0.1,
    triggerOnce: true,
    rootMargin: "0px 0px -100px 0px",
  });

  const stats = [
    {
      value: 50,
      suffix: "+",
      label: "Active Clients",
      icon: <GroupIcon sx={{ fontSize: { xs: "20px", md: "24px" } }} />,
    },
    {
      value: 1,
      suffix: "M+",
      label: "Reports Generated",
      icon: <AssessmentIcon sx={{ fontSize: { xs: "20px", md: "24px" } }} />,
    },
    {
      value: 15,
      suffix: "+",
      label: "Languages",
      icon: <TranslateIcon sx={{ fontSize: { xs: "20px", md: "24px" } }} />,
    },
    {
      value: 99.9,
      suffix: "%",
      label: "Accuracy",
      icon: <CheckCircleIcon sx={{ fontSize: { xs: "20px", md: "24px" } }} />,
    },
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15,
      },
    },
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 30, scale: 0.9 },
    visible: {
      opacity: 1,
      y: 0,
      scale: 1,
      transition: {
        duration: 0.6,
        ease: "easeOut",
      },
    },
  };

  return (
    <Box
      ref={ref}
      sx={{
        width: "100%",
        padding: {
          xs: "60px 16px",
          sm: "80px 24px",
          md: "50px 48px",
        },
        backgroundColor: "transparent",
      }}
    >
      <Box
        sx={{
          maxWidth: "1400px",
          margin: "0 auto",
        }}
      >
        <motion.div
          variants={containerVariants}
          initial="hidden"
          animate={inView ? "visible" : "hidden"}
        >
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)", md: "repeat(4, 1fr)" },
              gap: { xs: "16px", md: "20px" },
            }}
          >
            {stats.map((stat, index) => (
              <motion.div key={index} variants={itemVariants}>
                <Box
                  sx={{
                    background: "linear-gradient(135deg, #022674 0%, #1E4DB7 100%)",
                    borderRadius: "12px",
                    padding: { xs: "20px 16px", md: "24px 20px" },
                    textAlign: "center",
                    boxShadow: "0 4px 16px rgba(2, 38, 116, 0.2)",
                    border: "1px solid rgba(255, 255, 255, 0.1)",
                    position: "relative",
                    overflow: "hidden",
                    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                    "&:hover": {
                      transform: "translateY(-4px)",
                      boxShadow: "0 8px 24px rgba(2, 38, 116, 0.3)",
                      border: "1px solid rgba(255, 255, 255, 0.2)",
                    },
                    // Shiny effect overlay
                    "&::after": {
                      content: '""',
                      position: "absolute",
                      top: "-50%",
                      left: "-50%",
                      width: "200%",
                      height: "200%",
                      background: "linear-gradient(45deg, transparent 30%, rgba(255, 255, 255, 0.3) 50%, transparent 70%)",
                      opacity: 0,
                      transition: "opacity 0.3s ease",
                      pointerEvents: "none",
                    },
                    "&:hover::after": {
                      opacity: 1,
                      animation: `${shine} 0.6s ease-in-out`,
                    },
                  }}
                >
                  {/* Icon */}
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      marginBottom: { xs: "8px", md: "12px" },
                      color: "white",
                    }}
                  >
                    {stat.icon}
                  </Box>
                  
                  <Typography
                    component="div"
                    sx={{
                      fontSize: { xs: "24px", sm: "28px", md: "32px" },
                      fontWeight: 800,
                      fontFamily: "'Poppins', sans-serif",
                      color: "white",
                      marginBottom: { xs: "6px", md: "8px" },
                      lineHeight: 1.1,
                      letterSpacing: "-0.5px",
                    }}
                  >
                    <CountUp
                      end={stat.value}
                      suffix={stat.suffix}
                      duration={2}
                      isInView={inView}
                    />
                  </Typography>
                  <Typography
                    sx={{
                      fontSize: { xs: "12px", md: "14px" },
                      color: "rgba(255, 255, 255, 0.9)",
                      fontWeight: 500,
                      lineHeight: 1.4,
                      letterSpacing: "0.2px",
                    }}
                  >
                    {stat.label}
                  </Typography>
                </Box>
              </motion.div>
            ))}
          </Box>
        </motion.div>
      </Box>
    </Box>
  );
};

export default StatisticsSection;

